import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import './BatchList.css';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const BatchList = () => {
  const { userData } = useUser();
  const location = useLocation();
  const academyId = location.state?.academyId;
  const { selectedCenter, setSelectedCenter } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('auth-token');

  const centerToUse = academyId ?? selectedCenter;


  const fetchBatchesData = async () => {
    setLoading(true)
    setError('')
    if (centerToUse) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ACADEMY_COACHES}/${centerToUse}/batches`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();

        if (result.success) {
          const fetchedBatches = result.data.batches;
          const fetchedBatchesStrength = result.data.batchesStrength;

          const updatedBatches = fetchedBatches.map((batch) => {
            const proficiencyValue = batch.proficiencyType?.toLowerCase() === 'basic' ? 'BEGINNER' : batch.proficiencyType || 'Unknown';
            const batchDetails = {
              id: batch.id,
              name: batch.name || 'Unnamed Batch',
              proficiency: proficiencyValue,
              capacity: batch.numberOfPlayers,
              currentStrength: fetchedBatchesStrength[batch.id] || 0,
              monthlyFees: batch.monthlyFees || 0,
            };
            return batchDetails;
          });


          setBatches(updatedBatches);
        }
      } catch (error) {

        setError('Error fetching data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };


  }
  useEffect(() => {
    fetchBatchesData();
  }, [selectedCenter]);
  const headers = [
    { label: "Name", key: "name" },
    { label: "Proficiency", key: "proficiency" },
    { label: "Capacity", key: "capacity" },
    { label: "Current Strength", key: "currentStrength" },
    { label: "Monthly Fees", key: "monthlyFees" },
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [action, setAction] = useState('');
  const [formData, setFormData] = useState({ name: '', proficiency: '', capacity: '', currentStrength: '', monthlyFees: '' });
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const toggleDropdown = (playerId, e) => {
    if (dropdownOpen === playerId) {
      return setDropdownOpen(null);
    }

    const { bottom, left } = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: bottom + window.scrollY, left: left + window.scrollX });
    setDropdownOpen(playerId);
  };
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleNewBatch = () => {
    navigate('/createNewBatch');
  };

  const handleDisableBatch = async (batchId) => {
    const confirmResult = await Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, disable it!',
      cancelButtonText: 'No, cancel!',
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_DISABLE_BATCH}/${batchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();


      if (response.ok && result.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Batch Disabled successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        const errorMessage = result.data?.errorMessage || result.success_message || 'Failed to disable batch';

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {

    }
  };


  const filteredBatches = batches.filter((batch) =>
    Object.values(batch)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;

    return [...data].sort((a, b) => {
      let valueA = a[key] ?? "";
      let valueB = b[key] ?? "";

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      return direction === "ascending" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };
  const sortedData = sortData(filteredBatches);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.custom-dropdown-player');
      if (dropdown && !dropdown.contains(event.target) && !event.target.closest('.dots-btn-player')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleBatchClick = (event, batchId) => {
    const url = `/batchPlayerList`;
    const query = new URLSearchParams({ batchId }).toString();
  
    if (event.metaKey || event.ctrlKey) {
      window.open(`${url}?${query}`, "_blank");
    } else {
      navigate(`${url}?${query}`);
    }
  };
  
  useEffect(() => {
    const closeOnScroll = () => setDropdownOpen(null);

    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);

  const totalEntries = filteredBatches.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  const handleNavigate = (event, url) => {
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        {hasPrivilege(userData, 'COACHING', 'BATCH_LIST_MODIFY') && (
          <button
            className="create-button-new"
            onClick={handleNewBatch}
          >
            <span className="create-button__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="23"
                height="23"
                className="create-button__svg"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="white"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>

            <span className="create-button__label">
              Create Batch
            </span>

          </button>)}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchBatchesData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredBatches.length === 0 && (
        <div className="error-message">No data available</div>
      )}
      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'COACHING', 'BATCH_LIST_MODIFY') && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((batch) => (
              <tr key={batch.id}>
                <td>
                  {hasPrivilege(userData, 'COACHING', 'BATCH_LIST_MODIFY') ? (
                   <span
                   onClick={(event) => handleBatchClick(event, batch.id)}
                   style={{ cursor: 'pointer', color: 'blue', textDecoration: 'none' }}
                 >
                   {batch.name}
                 </span>
                 
                  ) : (
                    <span>{batch.name}</span>
                  )}
                </td>
                <td>{batch.proficiency}</td>
                <td>{batch.capacity}</td>
                <td>{batch.currentStrength}</td>
                <td>{batch.monthlyFees}</td>
                {hasPrivilege(userData, 'COACHING', 'BATCH_LIST_MODIFY') && (
                  <td className="sport-actions-column">
                    <button
                      className="dots-btn-player"
                      onClick={(e) => toggleDropdown(batch.id, e)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "24px",
                      }}
                    >
                      &#x22EE;
                    </button>
                    {dropdownOpen === batch.id && (
                      <div
                        className="custom-dropdown-player"
                        style={{
                          position: 'absolute',
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                          minWidth: '150px'
                        }}
                      >
                        <button
                          className="dropdown-item"
                          onClick={(event) => handleNavigate(event, `/batchPlayerList?batchId=${batch.id}`)}
                        >
                          Player List
                        </button>

                        <button
                          className="dropdown-item"
                          onClick={(event) => handleNavigate(event, `/createNewBatch?batchId=${batch.id}`)}
                        >
                          Edit
                        </button>
                        <button className="dropdown-item" onClick={() => handleDisableBatch(batch.id)}>Disable</button>
                        <button
                          className="dropdown-item"
                          onClick={(event) => handleNavigate(event, `/playerpaymentDetail?batchId=${batch.id}`)}
                        >
                          Batch Player Payment Details
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BatchList;
